import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { exchangeSessionId } from "@/lib/auth";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Handles return from Emergent Google OAuth.
 * URL fragment looks like: #session_id=abc123
 * Exchanges it server-side and routes the user forward.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("Missing session id in URL.");
      return;
    }
    const sid = decodeURIComponent(m[1]);
    (async () => {
      try {
        const data = await exchangeSessionId(sid);
        await refresh();
        // Auto-claim referral code if the user landed via a /?ref=CODE link
        // (stored in localStorage by Landing.jsx before the OAuth redirect).
        try {
          const ref = localStorage.getItem("cartiq_referral_code");
          if (ref) {
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/claim`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ code: ref }),
            }).catch(() => {});
            localStorage.removeItem("cartiq_referral_code");
          }
        } catch {
          /* non-fatal */
        }
        // Clean the hash and route forward
        window.history.replaceState({}, "", window.location.pathname);
        if (data?.meta?.latestReportId) {
          navigate(`/dashboard/${data.meta.latestReportId}`, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (e) {
        console.error("auth callback failed", e);
        setError("Sign-in failed. Please try again.");
      }
    })();
  }, [navigate, refresh]);

  return (
    <div
      className="min-h-screen bg-[#0A0A0F] text-[#F0EDE8] flex items-center justify-center"
      data-testid="auth-callback-page"
    >
      <div className="text-center space-y-4">
        {!error ? (
          <>
            <div className="w-10 h-10 mx-auto border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
            <div className="text-sm uppercase tracking-[0.22em] text-[#888]">
              Signing you in…
            </div>
          </>
        ) : (
          <>
            <div className="text-sm uppercase tracking-[0.22em] text-[#E24B4A]">
              {error}
            </div>
            <button
              type="button"
              className="btn-saffron"
              onClick={() => navigate("/")}
              data-testid="auth-callback-retry"
            >
              Back to home
            </button>
          </>
        )}
      </div>
    </div>
  );
}
